/**
 * @module state/fetchSagas
 * Utility sagas for API requests to appserver
 * @see {@link module:lib/appserver/request} for actual requests
 */

import { select, call, put } from "redux-saga/effects";
import { DEBUG_MOBILE_NUMBER } from "lib/native";
import * as appserver from "lib/appserver/request";

import { getAPISimulator } from "state/demoMode";

import { getMobileNumber, getDeviceToken } from "state/app/selectors";
import { forceRegistration } from "state/app/actions";

function* getUserIdentity() {
  const mobileNumber = yield select(getMobileNumber);
  const deviceToken = yield select(getDeviceToken);
  // DEBUG: swap these returns to test demo mode when testing locally
  return { mobileNumber, deviceToken };
  // return { mobileNumber: DEBUG_MOBILE_NUMBER, deviceToken };
}

/**
 * Generic request - wraps a more specific appserver API function
 * Injects mobileNumber and deviceToken arguments
 * Extracts data from response object
 * Handles certain API exceptions, but always re-throws too
 * @param {Function} appserverAPI the actual request function to call
 * @param {...} args arguments to `appserverAPI`
 * @returns {Object} response data (JSend root, i.e. { data, status })
 */
function* sendRequest(appserverAPI, ...args) {
  
  const { mobileNumber, deviceToken } = yield call(getUserIdentity);
  
  // detect demo mode and swap API call for simulation
  const isDemoMode = mobileNumber === DEBUG_MOBILE_NUMBER;
  const apiHandler = isDemoMode ? getAPISimulator(appserverAPI) : appserverAPI;
  
  try {
    
    // call API and return response
    const response = yield call(apiHandler, mobileNumber, deviceToken, ...args);
    return response.data;
    
  } catch (error) {
    
    // unknown device means we should force registration immediately
    // TODO: proper instanceof (when babel is fixed)
    if (error.type === appserver.UnknownDeviceError.type) {
      yield put( forceRegistration("This device is no longer registered. Please re-register.") );
    }
    
    // add request/response details if possible
    if (error.response !== undefined) {
      const { config, status, data } = error.response;
      error.details = {
        request_url: config.url,
        request_data: config.data,
        response_status: status,
        response_body: data
      };
    }
    
    throw error;
    
  }
}

/**
 * GetMyApps() but it's a saga
 * @returns {object} response data
 * @see {@link sendRequest}
 */
export function* sendAvailableProductsRequest() {
  return yield call(sendRequest, appserver.availableProductsRequest);
}

/**
 * ServiceAppServerDataRequest() but it's a saga
 * @param {string} application teachers/school_money
 * @param {string} request_cmd passed to ServiceAppServerDataRequest
 * @param {Object} otherArgs additional arguments to ServiceAppServerDataRequest
 * @returns {object} response data
 * @see {@link sendRequest}
 */
export function* sendDataRequest(application, request_cmd, otherArgs) {
  const args = { request_cmd, ...otherArgs };
  return yield call(sendRequest, appserver.dataRequest, application, args);
}

/**
 * T2P-specific wrapper for sendDataRequest
 * @see {@link sendDataRequest}
 */
export function* sendTeachersDataRequest(requestCmd, args) {
  return yield call(sendDataRequest, "teachers", requestCmd, args);
}

/**
 * SM-specific wrapper for sendDataRequest
 * @see {@link sendDataRequest}
 */
export function* sendSchoolMoneyDataRequest(requestCmd, args) {
  return yield call(sendDataRequest, "school_money", requestCmd, args);
}



// WEBPACK FOOTER //
// ./src/state/fetchSagas.js